import { useEffect, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useCreateCompany } from '@/api/hooks';

// Autotask companyType — restricted to the two values relevant when creating
// a new client from an opportunity. Defaults to Customer.
const CLIENT_TYPES = [
  { value: 1, label: 'Customer' },
  { value: 2, label: 'Lead' },
] as const;

// Client Category (companyCategoryID) is not shown — it always mirrors Client Type, so we
// infer it. IDs are hardcoded (the API user can't read the CompanyCategories entity) and
// fixed in the Synfo tenant: companyType 1 (Customer) → 1, companyType 2 (Lead) → 110.
const CATEGORY_BY_TYPE: Record<number, number> = { 1: 1, 2: 110 };

const SELECT_CLS =
  'flex h-9 w-full rounded-md border border-input bg-background text-foreground px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-populates the company name (typically the text the user searched for). */
  initialName?: string;
  /** Called with the newly created company so the caller can select it. */
  onCreated: (company: { id: number; name: string }) => void;
}

export function CreateCompanyDialog({ open, onOpenChange, initialName = '', onCreated }: Props) {
  const createCompany = useCreateCompany();

  const [name, setName] = useState(initialName);
  const [companyType, setCompanyType] = useState<number>(1); // Customer
  const [phone, setPhone] = useState('');

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (open) {
      setName(initialName);
      setCompanyType(1);
      setPhone('');
    }
  }, [open, initialName]);

  const canSubmit =
    name.trim().length > 0 && phone.trim().length > 0 && !createCompany.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    createCompany.mutate(
      {
        companyName: name.trim(),
        companyType,
        companyCategoryID: CATEGORY_BY_TYPE[companyType] ?? 1,
        phone: phone.trim(),
      },
      {
        onSuccess: (data) => {
          const id = (data as any)?.itemId ?? (data as any)?.item?.id ?? (data as any)?.id;
          if (!id) {
            toast.error('Company created but no ID returned');
            return;
          }
          toast.success(`Created ${name.trim()}`);
          onCreated({ id, name: name.trim() });
          onOpenChange(false);
        },
        onError: (err) =>
          toast.error('Failed to create company', {
            description: err instanceof Error ? err.message : 'Unknown error',
          }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New Company</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 pb-6">
          <Field label="Company Name" required>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder="Company name"
              required
            />
          </Field>

          <Field label="Client Type" required>
            <select
              value={companyType}
              onChange={(e) => setCompanyType(Number(e.target.value))}
              className={SELECT_CLS}
            >
              {CLIENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Phone Number" required>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={25}
              placeholder="e.g. 03 9000 0000"
              required
            />
          </Field>

          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {createCompany.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Create Company
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-muted-foreground">
        {label}{required && <span className="ml-0.5 text-destructive">*</span>}
      </label>
      {children}
    </div>
  );
}
